import React, { useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Table, Input, Label, Row, Col } from "reactstrap";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { v4 as uuidv4 } from "uuid";

// Sube los renglones de una captura (modelo + color + talla + cantidad) desde
// un Excel. El layout se descarga del mismo boton, ya con los modelos de la
// empresa elegida para que el cliente copie los codigos tal cual.
//
// Reglas: MODELO y CANTIDAD son obligatorios; COLOR y TALLA vacios quedan en
// NA. Un modelo que no esta en la lista (o que es de otra empresa) se marca
// como error y no se carga. El precio vacio toma el del catalogo.
const txt = (v) => String(v === undefined || v === null ? "" : v).trim();
const nu = (v) => Number(String(v ?? "").toString().replace(/[$,\s]/g, "")) || 0;
const norm = (v) => txt(v).toUpperCase();
const num = (n) => new Intl.NumberFormat("en-US").format(Number(n) || 0);
const nombreColor = (c) => (c ? (c.name) : "");

// [encabezado, obligatorio?, ejemplo]
const COLUMNAS = [
  ["MODELO", true, "EJEMPLO-BORRAR"],
  ["COLOR", false, "NEGRO"],
  ["TALLA", false, "CH"],
  ["CANTIDAD", true, 100],
  ["PRECIO", false, 0],
];

function ImportarLineasModal({ abierto, cerrar, articulos = [], coloresCat = [], tallasCat = [], moneda = "USD", precioPorDefecto, onCargado }) {
  const [filas, setFilas] = useState([]);
  const [archivo, setArchivo] = useState("");

  const busca = (lista, valor, campo = "name") => {
    const t = norm(valor);
    if (!t) return null;
    return (lista || []).find((x) => norm(x[campo]) === t
      || (x.codigo && norm(`${x.codigo} ${x[campo]}`) === t)
      || (x.codigo && norm(x.codigo) === t)
      || (x.alias || []).some((a) => norm(a) === t)) || null;
  };
  // Un modelo se encuentra por codigo, por "codigo nombre" o por nombre
  const buscaArticulo = (valor) => {
    const t = norm(valor);
    if (!t) return null;
    return articulos.find((a) => norm(a.codigo) === t
      || norm(`${a.codigo} ${a.nombre}`) === t
      || norm(a.nombre) === t) || null;
  };
  // Los colores y tallas del modelo pueden venir poblados o como ids
  const resuelve = (lista, catalogo) => {
    if (!lista || !lista.length) return catalogo;
    return lista.map((x) => (x && x.name ? x : catalogo.find((c) => String(c._id) === String(x && x._id ? x._id : x)))).filter(Boolean);
  };

  function descargaLayout() {
    const wb = XLSX.utils.book_new();
    const hoja = XLSX.utils.aoa_to_sheet([
      COLUMNAS.map(([c, ob]) => (ob ? `${c} *` : c)),
      COLUMNAS.map(([, , ej]) => ej),
    ]);
    hoja["!cols"] = COLUMNAS.map(([c]) => ({ wch: Math.max(14, c.length + 6) }));
    XLSX.utils.book_append_sheet(wb, hoja, "Renglones");
    // Hoja con los modelos que se pueden capturar, con sus colores y tallas
    const modelos = XLSX.utils.aoa_to_sheet([
      ["CODIGO", "NOMBRE", "COLORES", "TALLAS"],
      ...articulos.map((a) => [
        a.codigo, a.nombre,
        resuelve(a.colores, coloresCat).map((c) => nombreColor(c)).join(" | "),
        resuelve(a.tallas, tallasCat).map((t) => t.name).join(" | "),
      ]),
    ]);
    modelos["!cols"] = [{ wch: 18 }, { wch: 40 }, { wch: 60 }, { wch: 40 }];
    XLSX.utils.book_append_sheet(wb, modelos, "Modelos");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ["COMO LLENAR ESTE LAYOUT"],
      [],
      ["1", "Un renglon por modelo, color y talla, empezando en el renglon 3. El renglon 2 es un ejemplo: se puede borrar, y aunque se deje no se captura."],
      ["2", "Las columnas con * son obligatorias: MODELO y CANTIDAD."],
      ["3", "En MODELO copia el CODIGO de la hoja Modelos (tambien se acepta 'codigo nombre')."],
      ["4", "COLOR y TALLA se copian de la hoja Modelos. Si se dejan vacios se usa NA."],
      ["5", "Si el mismo modelo, color y talla viene en dos renglones, las cantidades se suman."],
      ["6", `PRECIO va en ${moneda} y es por pieza; si se deja vacio se toma el del catalogo.`],
      ["7", "No cambies los nombres de las columnas ni el orden."],
    ]), "Instrucciones");
    XLSX.writeFile(wb, "LayoutCaptura.xlsx");
  }

  function leeArchivo(file) {
    if (!file) return;
    setArchivo(file.name);
    const lector = new FileReader();
    lector.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const hoja = wb.Sheets[wb.SheetNames[0]];
        setFilas(revisa(XLSX.utils.sheet_to_json(hoja, { defval: "" })));
      } catch (err) {
        Swal.fire({ icon: "error", title: "No se pudo leer el archivo", text: "Usa el layout que se descarga de este mismo boton." });
      }
    };
    lector.readAsArrayBuffer(file);
  }

  function revisa(crudas) {
    const dame = (f, col) => {
      const k = Object.keys(f).find((x) => norm(x).replace(/\*/g, "").trim() === col);
      return k ? txt(f[k]) : "";
    };
    return crudas.map((f, i) => {
      const modelo = dame(f, "MODELO");
      const colorTxt = dame(f, "COLOR");
      const tallaTxt = dame(f, "TALLA");
      const cantidad = nu(dame(f, "CANTIDAD"));
      const fila = { renglon: i + 2, modelo, color: colorTxt, talla: tallaTxt, cantidad };
      // El renglon de ejemplo del layout se ignora aunque no lo borren
      if (!modelo && !colorTxt && !tallaTxt && !cantidad) return { ...fila, estado: "vacio" };
      if (norm(modelo).startsWith("EJEMPLO")) return { ...fila, estado: "vacio" };
      const articulo = buscaArticulo(modelo);
      if (!articulo) return { ...fila, estado: "error", detalle: `El modelo "${modelo}" no existe o es de otra empresa` };
      if (!(cantidad > 0)) return { ...fila, estado: "error", detalle: "La cantidad tiene que ser mayor a 0" };
      // Vacio = NA; escrito que no existe = error
      const color = colorTxt ? busca(coloresCat, colorTxt) : busca(coloresCat, "NA");
      if (!color) return { ...fila, estado: "error", detalle: colorTxt ? `El color "${colorTxt}" no existe` : "No hay color NA en el catalogo" };
      const talla = tallaTxt ? busca(tallasCat, tallaTxt) : busca(tallasCat, "NA");
      if (!talla) return { ...fila, estado: "error", detalle: tallaTxt ? `La talla "${tallaTxt}" no existe` : "No hay talla NA en el catalogo" };
      const precioTxt = dame(f, "PRECIO");
      const precio = precioTxt ? nu(precioTxt) : (precioPorDefecto ? precioPorDefecto(articulo) : (articulo.costoUSD || 0));
      return { ...fila, estado: "ok", articulo, color, talla, precio,
        detalle: `${nombreColor(color)} · ${talla.name} · ${num(cantidad)} pzas` };
    }).filter((f) => f.estado !== "vacio");
  }

  // Arma los renglones de captura: uno por modelo, con su reja de color y talla
  function arma(buenas) {
    const porArt = {};
    buenas.forEach((f) => {
      const id = String(f.articulo._id);
      if (!porArt[id]) {
        const cols = resuelve(f.articulo.colores, coloresCat);
        const tals = resuelve(f.articulo.tallas, tallasCat);
        porArt[id] = { articulo: f.articulo, cols: [...cols], tals: [...tals], celdas: {}, precio: f.precio };
      }
      const a = porArt[id];
      // Un color o talla que no traia el modelo se agrega a la reja
      if (!a.cols.some((c) => String(c._id) === String(f.color._id))) a.cols.push(f.color);
      if (!a.tals.some((t) => String(t._id) === String(f.talla._id))) a.tals.push(f.talla);
      const k = `${f.color._id}|${f.talla._id}`;
      a.celdas[k] = (a.celdas[k] || 0) + f.cantidad;
    });
    // El Autocomplete de la captura muestra la opcion con su campo junta
    const comoOpcion = (art) => {
      const inicial = String(art.codigo || "")[0]?.toUpperCase() || "";
      return { ...art, junta: `${art.codigo}${art.nombre}`, firstLetter: /[0-9]/.test(inicial) ? "0-9" : inicial };
    };
    return Object.values(porArt).map((a) => {
      const tc = [];
      a.cols.forEach((c) => a.tals.forEach((t) => {
        tc.push({ colores: c._id, tallas: t._id, cantidad: a.celdas[`${c._id}|${t._id}`] || 0 });
      }));
      const cantidad = tc.reduce((s, c) => s + (Number(c.cantidad) || 0), 0);
      const pzasCaja = Number(a.articulo.piezasPorCaja) || 0;
      return {
        id: uuidv4(),
        colores: "", tallas: "",
        articulos: a.articulo._id,
        articulosName: `${a.articulo.codigo} ${a.articulo.nombre}`,
        articulosNombre: comoOpcion(a.articulo),
        arrayColores: a.cols, arrayTallas: a.tals, tc,
        cantidad,
        piezasPorCaja: pzasCaja,
        cajas: pzasCaja > 0 ? (cantidad / pzasCaja).toFixed(2) : 0,
        precio: a.precio, precioMX: 0, tipoCambio: 0,
        total: cantidad * (Number(a.precio) || 0),
      };
    });
  }

  async function carga() {
    const buenas = filas.filter((f) => f.estado === "ok");
    if (!buenas.length) {
      Swal.fire({ icon: "warning", title: "Nada que cargar", text: "Ningun renglon del archivo se puede capturar." });
      return;
    }
    const fields = arma(buenas);
    const conError = filas.filter((f) => f.estado === "error").length;
    const piezas = fields.reduce((s, f) => s + f.cantidad, 0);
    const ok = await Swal.fire({
      icon: "question",
      title: `Se cargaran ${fields.length} modelo(s)`,
      html: `${num(piezas)} piezas en total.` + (conError ? `<br><b style="color:#b45309">${conError} renglon(es) con error se omiten.</b>` : "")
        + `<br><br>Esto reemplaza lo que tengas capturado.`,
      showCancelButton: true, confirmButtonText: "Si, cargar", cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    onCargado(fields);
    setFilas([]); setArchivo("");
    cerrar();
  }

  const buenas = filas.filter((f) => f.estado === "ok").length;
  const malas = filas.filter((f) => f.estado === "error").length;

  return (
    <Modal size="lg" isOpen={abierto} toggle={cerrar}>
      <ModalHeader toggle={cerrar}>Subir renglones desde layout</ModalHeader>
      <ModalBody>
        <Row>
          <Col md={5}>
            <Label style={{ fontWeight: 600 }}>1. LAYOUT</Label>
            <div>
              <Button size="sm" color="success" onClick={descargaLayout} disabled={!articulos.length}>
                <i className="fas fa-download" /> Descargar layout
              </Button>
            </div>
            <small style={{ color: "#64748b" }}>
              {articulos.length ? `Trae los ${articulos.length} modelo(s) de la empresa elegida.` : "Elige primero la empresa."}
            </small>
          </Col>
          <Col md={7}>
            <Label style={{ fontWeight: 600 }}>2. SUBE EL LAYOUT LLENO</Label>
            <Input type="file" accept=".xlsx,.xls" onChange={(e) => leeArchivo(e.target.files[0])} />
            <small style={{ color: "#64748b" }}>{archivo}</small>
          </Col>
        </Row>
        <br />
        {filas.length ? (
          <>
            <div style={{ marginBottom: 6 }}>
              <span style={{ color: "#15803d", fontWeight: 600 }}>{buenas} por cargar</span>
              {malas ? <span style={{ color: "#dc2626", fontWeight: 600, marginLeft: 14 }}>{malas} con error</span> : null}
            </div>
            <div style={{ maxHeight: 320, overflowY: "auto" }}>
              <Table size="sm" bordered>
                <thead>
                  <tr><th>Renglón</th><th>Modelo</th><th className="text-right">Cantidad</th><th>Estado</th></tr>
                </thead>
                <tbody>
                  {filas.map((f, i) => (
                    <tr key={i} style={{ background: f.estado === "error" ? "#fef2f2" : undefined }}>
                      <td>{f.renglon}</td>
                      <td>{f.modelo}</td>
                      <td className="text-right">{num(f.cantidad)}</td>
                      <td style={{ color: f.estado === "error" ? "#dc2626" : "#15803d" }}>{f.detalle}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        ) : (
          <p style={{ color: "#9ca3af" }}>Sube el layout lleno para ver qué se va a capturar.</p>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="success" onClick={carga} disabled={!buenas}>
          {buenas ? `Cargar ${buenas} renglón(es)` : "Cargar"}
        </Button>
        <Button color="secondary" onClick={cerrar}>Cerrar</Button>
      </ModalFooter>
    </Modal>
  );
}

export default ImportarLineasModal;
